import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { LOADING_STATUSES } from 'shared/constants/loadingStatuses'
import { aiApi, IAIAssistantChat } from 'shared/api/aiApi'
import { searchApi, SearchNode } from 'shared/api/searchApi'
import { RootState } from 'shared/config/store'
import { IFsNodeSchema, NodeTypes } from '@cloudike/web_files'
import { getFilesSdk } from 'shared/sdk/files'
import { removeFileFromUploadingThunk } from 'features/files-uploading/model/slice/filesUploadingSlice'
import { assetsApi } from 'shared/api/assetsApi'
import { setFieldOnAssetThunk } from 'features/assets/model/slice/assetsSlice'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import { t } from 'i18next'

import { AiAssistantState, AiChat, AiMessage } from '../types/aiAssistant'

const convertApiChatToInternalChat = (apiChat: IAIAssistantChat): AiChat => ({
  id: apiChat.id,
  title: apiChat.name,
  createdAt: apiChat.created,
  updatedAt: apiChat.updated,
  author_id: apiChat.author_id,
  author_name: apiChat.author_name,
  status: apiChat.status,
})

const convertApiMessageToInternalMessage = (apiMessage: any): AiMessage => ({
  id: apiMessage.id,
  content: apiMessage.message,
  timestamp: apiMessage.created,
  author_id: apiMessage.author_id,
  author_name: apiMessage.author_name,
  attachments: apiMessage.attachments,
})

const getDraftFromLocalStorage = (): Record<string, string> => {
  try {
    const saved = localStorage.getItem('ai-chat-drafts')
    return saved ? JSON.parse(saved) : {}
  } catch {
    return {}
  }
}

const saveDraftToLocalStorage = (drafts: Record<string, string>): void => {
  try {
    localStorage.setItem('ai-chat-drafts', JSON.stringify(drafts))
  } catch {
  }
}

const initialState: AiAssistantState = {
  chats: [],
  chatMessages: {},
  status: LOADING_STATUSES.IDLE,
  error: null,
  attachmentsModal: {
    isOpen: false,
    selectedSection: 'all-assets',
    selectedAttachments: [],
    loadingAssets: true,
    searchValue: '',
    searchResults: null,
    isSearching: false,
    assets: [],
    areAllAssetsFetched: false,
  },
  draftMessages: getDraftFromLocalStorage(),
  chatsLoading: false,
  messagesLoading: {},
  activePollingInterval: null,
  activePollingTimeout: null,
  chatsIdsToShowAnimatedMessage: [],
  attachments: [],
  system_hints: [],
  itemsMarkedAsUsefullForAiAssistant: {},
}

const generateChatTitle = (message: string) => {
  const plainText = message.replace(/&lt;/g, '<').replace(/&gt;/g, '>')
  const words = plainText.split(' ')
  return words.slice(0, 8).join(' ') + (words.length > 8 ? '...' : '')
}

const messagesChanged = (oldMessages: AiMessage[], newMessages: AiMessage[]): boolean => {
  if (oldMessages.length !== newMessages.length) return true

  for (let i = 0; i < oldMessages.length; i++) {
    if (oldMessages[i].id !== newMessages[i].id ||
      oldMessages[i].content !== newMessages[i].content) {
      return true
    }
  }

  return false
}

export const fetchChatsThunk = createAsyncThunk(
  'aiAssistant/fetchChats',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const userId = state.user.user?.family_user_id

      if (!userId) {
        return rejectWithValue('User not found')
      }

      const response = await aiApi.getChats(userId, { limit: 500, is_personal: true })
      const chats = response._embedded.ai_chats.map(convertApiChatToInternalChat)

      return chats
    } catch (error) {
      return rejectWithValue('Failed to fetch chats')
    }
  }
)

export const fetchChatInfoThunk = createAsyncThunk(
  'aiAssistant/fetchChatInfo',
  async ({ chatId }: { chatId: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const userId = state.user.user?.family_user_id

      if (!userId) {
        return rejectWithValue('User not found')
      }

      const response = await aiApi.getChatInfo(userId, chatId)
      const system_hints = response.system_hints

      dispatch(actions.setSystemHints(system_hints))
    } catch (error) {
      return rejectWithValue('Failed to fetch chat info')
    }
  }
)

export const fetchChatMessagesThunk = createAsyncThunk(
  'aiAssistant/fetchChatMessages',
  async ({ chatId }: { chatId: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const userId = state.user.user?.family_user_id

      if (!userId) {
        return rejectWithValue('User not found')
      }

      dispatch(actions.setMessagesLoading({ chatId, loading: true }))

      const response = await aiApi.getChatMessages(userId, chatId, { limit: 500, preview: true, preview_jwt: true })
      const messages = response._embedded.messages.map(convertApiMessageToInternalMessage)

      const aiState = state.aiAssistant as AiAssistantState
      const currentMessages = aiState.chatMessages[chatId] || []

      const lastMessage = [...messages].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()).at(-1)

      if (lastMessage && lastMessage.author_id !== -1) {
        dispatch(actions.addChatIdToShowAnimatedMessage(chatId))
      }

      if (messagesChanged(currentMessages, messages)) {
        dispatch(actions.setChatMessages({ chatId, messages }))
      }

      dispatch(actions.setMessagesLoading({ chatId, loading: false }))

      dispatch(startPollingThunk({ chatId }))

      return { chatId, messages }
    } catch (error) {
      dispatch(actions.setMessagesLoading({ chatId, loading: false }))
      return rejectWithValue('Failed to fetch messages')
    }
  }
)

export const pollChatMessagesThunk = createAsyncThunk(
  'aiAssistant/pollChatMessages',
  async ({ chatId }: { chatId: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const userId = state.user.user?.family_user_id

      if (!userId) {
        return rejectWithValue('User not found')
      }

      const response = await aiApi.getChatMessages(userId, chatId, { limit: 500, preview: true, preview_jwt: true })
      const newMessages = response._embedded.messages.map(convertApiMessageToInternalMessage)

      const aiState = state.aiAssistant as AiAssistantState
      const currentMessages = aiState.chatMessages[chatId] || []

      if (messagesChanged(currentMessages, newMessages)) {
        dispatch(aiAssistantSlice.actions.setChatMessages({ chatId, messages: newMessages }))

        const lastMessage = [...newMessages]
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
          .at(-1)

        if (lastMessage && lastMessage.author_id === -1) {
          return { hasNewMessages: true, chatId }
        }
      }

      return { hasNewMessages: false, chatId }
    } catch (error) {
      return rejectWithValue('Failed to poll messages')
    }
  }
)

export const startPollingThunk = createAsyncThunk(
  'aiAssistant/startPolling',
  async ({ chatId }: { chatId: string }, { dispatch, getState }) => {
    const state = getState() as RootState
    const aiState = state.aiAssistant as AiAssistantState

    if (aiState.activePollingInterval) {
      clearInterval(aiState.activePollingInterval)
    }

    if (aiState.activePollingTimeout) {
      clearTimeout(aiState.activePollingTimeout)
    }

    const pollInterval = setInterval(async () => {
      try {
        dispatch(pollChatMessagesThunk({ chatId }))
      } catch (error) {
        console.error('Polling error:', error)
      }
    }, 5000)

    dispatch(aiAssistantSlice.actions.setActivePollingInterval(pollInterval))

    return chatId
  }
)

export const stopPollingThunk = createAsyncThunk(
  'aiAssistant/stopPolling',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    const aiState = state.aiAssistant as AiAssistantState

    if (aiState.activePollingInterval) {
      clearInterval(aiState.activePollingInterval)
    }

    if (aiState.activePollingTimeout) {
      clearTimeout(aiState.activePollingTimeout)
    }

    dispatch(aiAssistantSlice.actions.clearActivePollingHandles())
  }
)

export const sendMessageThunk = createAsyncThunk(
  'aiAssistant/sendMessage',
  async ({ message, chatId }: { message: string; chatId?: string }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const userId = state.user.user?.family_user_id
      const systemHints = state.aiAssistant.system_hints

      const attachmentsUrls = state.aiAssistant.attachments.map(a => a._links.self.href)

      if (!userId) {
        return rejectWithValue('User not found')
      }

      let targetChatId = chatId
      let userMessageData: AiMessage

      if (!targetChatId) {
        const chatTitle = generateChatTitle(message)

        const newChatData: { name: string; message: string; attachments?: string[], system_hints?: string[] } = {
          name: chatTitle,
          message,
          system_hints: systemHints
        }

        if (attachmentsUrls.length > 0) {
          newChatData.attachments = attachmentsUrls
        }

        const newChatResponse = await aiApi.createChat(userId, newChatData)
        targetChatId = newChatResponse.id

        const newChat = convertApiChatToInternalChat(newChatResponse)
        dispatch(aiAssistantSlice.actions.addChat(newChat))
        dispatch(aiAssistantSlice.actions.clearDraftMessage('new'))

        const messagesResponse = await aiApi.getChatMessages(userId, targetChatId, { limit: 500, preview: true, preview_jwt: true })
        const messages = messagesResponse._embedded.messages.map(convertApiMessageToInternalMessage)

        dispatch(aiAssistantSlice.actions.setChatMessages({ chatId: targetChatId, messages }))

        userMessageData = messages.find(m => m.author_id > 0)!
      } else {
        const params: { message: string; attachments?: string[] } = { message }

        if (attachmentsUrls.length > 0) {
          params.attachments = attachmentsUrls
        }

        const userMessageResponse = await aiApi.createChatMessage(userId, targetChatId, params)

        userMessageData = convertApiMessageToInternalMessage(userMessageResponse)

        const aiState = state.aiAssistant as AiAssistantState
        const currentMessages = aiState.chatMessages[targetChatId] || []
        dispatch(aiAssistantSlice.actions.setChatMessages({
          chatId: targetChatId,
          messages: [...currentMessages, userMessageData]
        }))
      }

      dispatch(aiAssistantSlice.actions.clearDraftMessage(targetChatId))
      dispatch(aiAssistantSlice.actions.clearAttachments())
      dispatch(aiAssistantSlice.actions.addChatIdToShowAnimatedMessage(targetChatId))

      return { chatId: targetChatId }
    } catch (error) {
      return rejectWithValue('Failed to send message')
    }
  }
)

export const fetchAiAssistantSearchResultsThunk = createAsyncThunk(
  'aiAssistant/fetchAiAssistantSearchResultsThunk',
  async ({ value }: { value: string }, { getState, dispatch }) => {
    const state = getState() as RootState
    const userId = state.user.user.family_user_id

    dispatch(actions.setAttachmentsLoading(true))

    const response = await searchApi.search(userId, { labels_or_name_pattern: value + '*' })

    const assets = response._embedded.nodes.filter((node: SearchNode) => node.type === 'file')

    dispatch(actions.setAttachmentsSearchResults(assets))
    dispatch(actions.setAttachmentsLoading(false))
  }
)

let paginator: any

export const fetchFirstAttachmentsAssetsThunk = createAsyncThunk('aiAssistant/fetchFirstAttachmentsAssetsThunk', async ({ boardId }: { boardId: string }, { dispatch, getState }) => {
  const sdk = getFilesSdk()
  const state = getState() as RootState
  const userState = state.user
  const assetRootBoardId = userState.assetRootBoardId || null
  const { familyFolderOperationsService: folderOperationsService } = sdk

  dispatch(actions.setAttachmentsLoading(true))

  const nodesOptions = {
    embedded: true,
    preview_jwt: true,
    preview: true,
    type__in: ['file'],
    total_count: true
  }

  const pageSize = 40

  paginator = folderOperationsService.getChildNodesPaginator(pageSize, boardId || assetRootBoardId, nodesOptions)

  const response = await paginator.next()

  const assets = response?.data?._embedded?.nodes || []
  const filteredNodes = assets
    .filter((node: any) => !node.is_trashed)
    .filter((node: IFsNodeSchema) => node.type === NodeTypes.FILE)

  dispatch(actions.setAttachmentsAssets(filteredNodes))
  dispatch(actions.setAttachmentsAreAllAssetsFetched(!response.data._links.next))
  dispatch(actions.setAttachmentsLoading(false))
})

export const fetchNextAttachmentsAssetsThunk = createAsyncThunk('aiAssistant/fetchNextAttachmentsAssetsThunk', async (_, { dispatch }) => {
  const response = await paginator.next()

  const assets = response?.data?._embedded?.nodes || []

  const filteredNodes = assets
    .filter((node: any) => !node.is_trashed)
    .filter((node: IFsNodeSchema) => node.type === NodeTypes.FILE)

  dispatch(actions.setAttachmentsLoading(false))
  dispatch(actions.addAttachmentsAssets(filteredNodes))
  dispatch(actions.setAttachmentsAreAllAssetsFetched(!response.data._links.next))
})

export const fetchAttachmentsBoardAllAssetsThunk = createAsyncThunk('aiAssistant/fetchAttachmentsBoardAllAssetsThunk',
  async ({ boardId }: { boardId: string }, { dispatch }) => {
    try {
      dispatch(actions.setAttachmentsLoading(true))

      const sdk = getFilesSdk()
      const { familyFolderOperationsService: folderOperationsService } = sdk
      const nodesOptions = {
        embedded: true,
        preview_jwt: true,
        preview: true,
        type__in: ['link'],
        order_by: '-created',
        // trash_state: 'live'
      }

      const delay = 100
      const pageSize = 500

      const allNodes = await folderOperationsService.getAllChildNodes(
        boardId,
        nodesOptions,
        pageSize,
        delay
      )
      const filteredNodes = allNodes
        .filter((node: IFsNodeSchema) => !node.is_trashed)
        .filter((node: IFsNodeSchema) => node.type === NodeTypes.LINK)

      dispatch(actions.setAttachmentsAssets(filteredNodes))
      dispatch(actions.setAttachmentsAreAllAssetsFetched(true))
      dispatch(actions.setAttachmentsLoading(false))
    } catch (error) {
      console.log('Error in fetchBoardAllAssetsThunk:', error)
      throw error
    }
  })

export const renameChatThunk = createAsyncThunk(
  'aiAssistant/renameChat',
  async ({ chatId, name }: { chatId: string, name: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const userId = state.user.user?.family_user_id
      if (!userId) {
        return rejectWithValue('User not found')
      }
      await aiApi.updateChat(userId, chatId, { name })
      return { chatId, name }
    } catch (error) {
      return rejectWithValue('Failed to rename chat')
    }
  },
)

export const setUseAssetsThunk = createAsyncThunk(
  'aiAssistant/setUseAssets',
  async ({ chatId, useAssets }: { chatId: string, useAssets: boolean }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const userId = state.user.user?.family_user_id

      await aiApi.updateChat(userId, chatId, { system_hints: useAssets ? ['use_documents'] : [] })

      dispatch(actions.setSystemHints(useAssets ? ['use_documents'] : []))
    } catch (error) {
      return rejectWithValue('Failed to rename chat')
    }
  }
)

export const deleteChatThunk = createAsyncThunk(
  'aiAssistant/deleteChat',
  async ({ chatId }: { chatId: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const userId = state.user.user?.family_user_id
      if (!userId) {
        return rejectWithValue('User not found')
      }
      await aiApi.deleteChat(userId, chatId)
      return { chatId }
    } catch (error) {
      return rejectWithValue('Failed to delete chat')
    }
  },
)

export const addAttachmentsFromUploadedFilesThunk = createAsyncThunk(
  'aiAssistant/addAttachmentsFromUploadedFilesThunk',
  async ({ files }: { files: any[] }, { dispatch, getState }) => {
    const state = getState() as RootState
    const userId = state.user.user?.family_user_id

    const nodes = await Promise.all(
      files.map(async (file: any) => {
        try {
          const node = await assetsApi.getAssetInfo(userId, file.nodeId, undefined, { preview: true, preview_jwt: true })

          return node
        } catch {
          return null
        }
      })
    )

    const validNodes = nodes.filter(Boolean)

    if (validNodes.length > 0) {
      dispatch(actions.addAttachments(validNodes))
    }

    files.forEach(file => {
      dispatch(removeFileFromUploadingThunk({
        id: file.id,
        name: file.name,
        size: typeof file.size === 'string' ? parseInt(file.size, 10) || 0 : 0,
        type: file.type,
        state: file.state
      }))
    })
  }
)

export const fetchAssetAiAssistantFieldsThunk = createAsyncThunk(
  'aiAssistant/fetchAssetAiAssistantFieldsThunk',
  async ({ nodeId }: { nodeId: string }, { dispatch, getState }) => {
    const state = getState() as RootState

    const userID = state.user?.user?.family_user_id || 0

    const response = await assetsApi.getAssetFields(userID, nodeId)

    const field = response._embedded.fields.find((field) => field?.field?.title === 'use_for_rag')
    const useForRagFieldValue = field?.value as boolean

    dispatch(actions.setItemsMarkedAsUsefullForAiAssistant({ nodeId, useForRagFieldValue, fieldId: field?.field?.id }))
  }
)

export const updateAssetAiAssistantUseForRagFieldThunk = createAsyncThunk(
  'aiAssistant/updateAssetAiAssistantUseForRagFieldThunk',
  async ({ nodeId, useForRagFieldValue }: { nodeId: string; useForRagFieldValue: boolean }, { dispatch, getState }) => {
    const state = getState() as RootState
    const itemsMarkedAsUsefullForAiAssistant = state.aiAssistant.itemsMarkedAsUsefullForAiAssistant
    const fieldId = itemsMarkedAsUsefullForAiAssistant[nodeId]?.fieldId

    const successCallback = () => {
      dispatch(actions.setItemsMarkedAsUsefullForAiAssistant({ nodeId, useForRagFieldValue }))

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: useForRagFieldValue ? t('l_notification_aivardAllowSuccess') : t('l_notification_aivardHideSuccess'),
        message: '',
      })
    }

    dispatch(setFieldOnAssetThunk({ id: nodeId, fieldID: fieldId, value: useForRagFieldValue, successCallback }))
  }
)

export const aiAssistantSlice = createSlice({
  name: 'aiAssistant',
  initialState,
  reducers: {
    addChat: (state, action: PayloadAction<AiChat>) => {
      state.chats.push(action.payload)
    },
    setChatMessages: (state, action: PayloadAction<{ chatId: string; messages: AiMessage[] }>) => {
      state.chatMessages[action.payload.chatId] = action.payload.messages
    },
    setMessagesLoading: (state, action: PayloadAction<{ chatId: string; loading: boolean }>) => {
      state.messagesLoading[action.payload.chatId] = action.payload.loading
    },
    setAttachmentsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.attachmentsModal.isOpen = action.payload
    },
    setAttachmentsModalSection: (state, action: PayloadAction<string>) => {
      state.attachmentsModal.selectedSection = action.payload
    },
    toggleAttachmentSelection: (state, action: PayloadAction<IFsNodeSchema>) => {
      const attachment = action.payload
      const selectedAttachments = state.attachmentsModal.selectedAttachments

      if (selectedAttachments.find(a => a.id === attachment.id)) {
        state.attachmentsModal.selectedAttachments = selectedAttachments.filter(a => a.id !== attachment.id)
      } else {
        state.attachmentsModal.selectedAttachments.push(attachment)
      }
    },
    clearAttachmentSelection: (state) => {
      state.attachmentsModal.selectedAttachments = []
    },
    setAttachmentsSearchValue: (state, action: PayloadAction<string>) => {
      state.attachmentsModal.searchValue = action.payload
    },
    setAttachmentsSearchResults: (state, action: PayloadAction<any[]>) => {
      state.attachmentsModal.searchResults = action.payload
    },
    setAttachmentsSearching: (state, action: PayloadAction<boolean>) => {
      state.attachmentsModal.isSearching = action.payload
    },
    clearAttachmentsSearchResults: (state) => {
      state.attachmentsModal.searchResults = null
    },
    setDraftMessage: (state, action: PayloadAction<{ chatId: string; message: string }>) => {
      state.draftMessages[action.payload.chatId] = action.payload.message
      saveDraftToLocalStorage(state.draftMessages)
    },
    clearDraftMessage: (state, action: PayloadAction<string>) => {
      delete state.draftMessages[action.payload]
      saveDraftToLocalStorage(state.draftMessages)
    },
    setActivePollingInterval: (state, action: PayloadAction<NodeJS.Timeout>) => {
      state.activePollingInterval = action.payload
    },
    setActivePollingTimeout: (state, action: PayloadAction<NodeJS.Timeout>) => {
      state.activePollingTimeout = action.payload
    },
    clearActivePollingHandles: (state) => {
      state.activePollingInterval = null
      state.activePollingTimeout = null
    },
    addChatIdToShowAnimatedMessage: (state, action: PayloadAction<string>) => {
      state.chatsIdsToShowAnimatedMessage.push(action.payload)
    },
    removeChatIdFromShowAnimatedMessage: (state, action: PayloadAction<string>) => {
      state.chatsIdsToShowAnimatedMessage = state.chatsIdsToShowAnimatedMessage.filter(id => id !== action.payload)
    },
    setAttachmentsAssets: (state, action: PayloadAction<IFsNodeSchema[]>) => {
      state.attachmentsModal.assets = action.payload
    },
    addAttachmentsAssets: (state, action: PayloadAction<IFsNodeSchema[]>) => {
      state.attachmentsModal.assets.push(...action.payload)
    },
    setAttachmentsAreAllAssetsFetched: (state, action: PayloadAction<boolean>) => {
      state.attachmentsModal.areAllAssetsFetched = action.payload
    },
    setAttachmentsLoading: (state, action: PayloadAction<boolean>) => {
      state.attachmentsModal.loadingAssets = action.payload
    },
    setAttachments: (state, action: PayloadAction<IFsNodeSchema[]>) => {
      state.attachments = action.payload
    },
    addAttachments: (state, action: PayloadAction<IFsNodeSchema[]>) => {
      const filteredPayload = action.payload.filter(a => !state.attachments.find(a2 => a2.id === a.id))

      state.attachments.push(...filteredPayload)
    },
    removeAttachment: (state, action: PayloadAction<string>) => {
      state.attachments = state.attachments.filter(a => a.id !== action.payload)
    },
    clearAttachments: (state) => {
      state.attachments = []
    },
    setSystemHints: (state, action: PayloadAction<string[]>) => {
      state.system_hints = action.payload
    },
    setItemsMarkedAsUsefullForAiAssistant: (state, action: PayloadAction<{ nodeId: string; useForRagFieldValue?: boolean, fieldId?: string }>) => {
      const newData: { useForRagFieldValue?: boolean, fieldId?: string } = {
        useForRagFieldValue: action.payload.useForRagFieldValue,
      }

      if (action.payload.fieldId) {
        newData.fieldId = action.payload.fieldId
      }

      state.itemsMarkedAsUsefullForAiAssistant[action.payload.nodeId] = {
        ...state.itemsMarkedAsUsefullForAiAssistant[action.payload.nodeId],
        ...newData,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatsThunk.pending, (state) => {
        state.chatsLoading = true
        state.status = LOADING_STATUSES.LOADING
      })
      .addCase(fetchChatsThunk.fulfilled, (state, action) => {
        state.chats = action.payload
        state.chatsLoading = false
        state.status = LOADING_STATUSES.SUCCEEDED
      })
      .addCase(fetchChatsThunk.rejected, (state, action) => {
        state.chatsLoading = false
        state.status = LOADING_STATUSES.FAILED
        state.error = action.payload as string
      })
      .addCase(renameChatThunk.fulfilled, (state, action) => {
        const { chatId, name } = action.payload
        const chat = state.chats.find(c => c.id === chatId)
        if (chat) {
          chat.title = name
        }
      })
      .addCase(deleteChatThunk.fulfilled, (state, action) => {
        state.chats = state.chats.filter(c => c.id !== action.payload.chatId)
      })
  },
})

export const { actions } = aiAssistantSlice
export const { reducer } = aiAssistantSlice